import {
  ALLOCATION_UNITS,
  AllocationUnit,
  ConversionSource,
  convertAllocationQuantity,
  normalizeQuantityUnit,
} from './dimensionConversion';
import { formatUnit, normalizeUnitCode } from './units';
import { formatLocalizedQuantity } from './numberFormatting';

export const COMMERCIAL_UNITS: readonly AllocationUnit[] = ALLOCATION_UNITS;
/** Active units offered for every new order, PZ, WZ and transport line. */
export const ACTIVE_QUANTITY_UNITS = ALLOCATION_UNITS;
/** Read-only compatibility for rows created before the dimensional-only scope. */
export const READABLE_QUANTITY_UNITS = ['pcs', 'm2', 'm3', 'kg', 'm', 'mb', 'l'] as const;
export type QuantityUnit = (typeof READABLE_QUANTITY_UNITS)[number];
// Every dimensional unit can be either side of the same conversion. There is
// no catalog/warehouse unit hidden behind this list.
export const COMMERCIAL_PRICING_CONVERSION_UNITS: readonly AllocationUnit[] = COMMERCIAL_UNITS;
export const MAX_CUSTOMER_PHYSICAL_QUANTITY = 1_000_000;
export const MAX_CUSTOMER_PRICING_QUANTITY = 99_999_999_999.999;
export const COMMERCIAL_CONVERSION_ERRORS = {
  INVALID_QUANTITY: 'INVALID_QUANTITY',
  INVALID_UNIT: 'INVALID_UNIT',
  UNSUPPORTED_PAIR: 'UNSUPPORTED_PAIR',
  MISSING_DIMENSIONS: 'MISSING_DIMENSIONS',
  INCONSISTENT_QUANTITIES: 'INCONSISTENT_QUANTITIES',
} as const;

export interface CommercialQuantityValue {
  physicalQuantity: number;
  physicalUnit: QuantityUnit;
  pricingQuantity: number;
  pricingUnit: QuantityUnit;
  /** Immutable dimensional snapshot carried by an already persisted line. */
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  /** Optional presentation conversion, independent from the unit used for price. */
  conversionQuantity?: number;
  conversionUnit?: AllocationUnit | null;
}

export interface QuantitySet {
  pcs: number | null;
  m2: number | null;
  m3: number | null;
}

interface FormatQuantitySetOptions {
  absolute?: boolean;
  fallbackQuantity?: number | null;
  fallbackUnit?: QuantityUnit | null;
  emptyText?: string;
}

export function isAllocationUnit(value: unknown): value is AllocationUnit {
  return (COMMERCIAL_UNITS as readonly unknown[]).includes(value);
}

export function asQuantityUnit(value: unknown, fallback: QuantityUnit = 'pcs'): QuantityUnit {
  const normalized = normalizeUnitCode(String(value || fallback))
    ?? normalizeQuantityUnit(String(value || fallback));
  return (READABLE_QUANTITY_UNITS as readonly string[]).includes(normalized)
    ? normalized as QuantityUnit
    : fallback;
}

export function asCommercialUnit(value: unknown, fallback: AllocationUnit = 'pcs'): AllocationUnit {
  const normalized = normalizeQuantityUnit(String(value || fallback));
  return (COMMERCIAL_UNITS as readonly string[]).includes(normalized)
    ? normalized as AllocationUnit
    : fallback;
}

export function roundCommercialQuantity(value: number): number {
  return Math.round((Number(value) + Number.EPSILON) * 1000) / 1000;
}

export function convertCommercialQuantity(
  quantity: number,
  sourceUnit: AllocationUnit,
  targetUnit: AllocationUnit,
  dimensions: ConversionSource,
): { value: number | null; exactValue: number | null; error: string | null } {
  const result = convertAllocationQuantity({
    ...dimensions,
    quantity,
    sourceUnit,
    targetUnit,
  });

  return {
    value: result.exactValue == null ? null : roundCommercialQuantity(result.exactValue),
    exactValue: result.exactValue,
    error: result.error,
  };
}

/**
 * Convert an entered amount through an immutable persisted quantity snapshot.
 * When no snapshot exists, dimensions provide the first conversion set. This
 * is the single frontend home for choosing between those two sources.
 */
export function convertCommercialQuantityUsingSnapshot(
  quantity: number,
  sourceUnit: AllocationUnit,
  targetUnit: AllocationUnit,
  dimensions: ConversionSource,
  storedSet: QuantitySet | null,
): { value: number | null; exactValue: number | null; error: string | null } {
  if (!storedSet) {
    return convertCommercialQuantity(quantity, sourceUnit, targetUnit, dimensions);
  }

  const sourceAmount = getQuantitySetValue(storedSet, sourceUnit);
  const targetAmount = getQuantitySetValue(storedSet, targetUnit);
  if (sourceAmount === null || targetAmount === null || sourceAmount === 0) {
    return {
      value: null,
      exactValue: null,
      error: COMMERCIAL_CONVERSION_ERRORS.MISSING_DIMENSIONS,
    };
  }

  const exactValue = Number(quantity) * targetAmount / sourceAmount;
  if (!Number.isFinite(exactValue)) {
    return {
      value: null,
      exactValue: null,
      error: COMMERCIAL_CONVERSION_ERRORS.INVALID_QUANTITY,
    };
  }
  return { value: roundCommercialQuantity(exactValue), exactValue, error: null };
}

function isPositiveCommercialQuantity(value: unknown): boolean {
  const quantity = Number(value);
  return Number.isFinite(quantity)
    && quantity >= 0.001
    && quantity <= MAX_CUSTOMER_PRICING_QUANTITY
    && Math.abs(quantity - roundCommercialQuantity(quantity)) < 1e-9;
}

/** Frontend mirror of backend validateCommercialQuantityPair. */
export function validateCommercialQuantityPair(
  physicalQuantity: number,
  physicalUnit: unknown,
  pricingQuantity: number,
  pricingUnit: unknown,
  dimensions: ConversionSource,
): { valid: boolean; error: string | null } {
  if (!isPositiveCommercialQuantity(physicalQuantity) || !isPositiveCommercialQuantity(pricingQuantity)) {
    return { valid: false, error: COMMERCIAL_CONVERSION_ERRORS.INVALID_QUANTITY };
  }
  if (!isAllocationUnit(physicalUnit) || !isAllocationUnit(pricingUnit)) {
    return { valid: false, error: COMMERCIAL_CONVERSION_ERRORS.INVALID_UNIT };
  }
  if (physicalUnit === pricingUnit) {
    return roundCommercialQuantity(physicalQuantity) === roundCommercialQuantity(pricingQuantity)
      ? { valid: true, error: null }
      : { valid: false, error: COMMERCIAL_CONVERSION_ERRORS.INCONSISTENT_QUANTITIES };
  }

  const forward = convertCommercialQuantity(
    physicalQuantity,
    physicalUnit,
    pricingUnit,
    dimensions,
  );
  if (forward.error || forward.exactValue === null) {
    return {
      valid: false,
      error: forward.error === 'MISSING_DIMENSIONS'
        ? COMMERCIAL_CONVERSION_ERRORS.MISSING_DIMENSIONS
        : COMMERCIAL_CONVERSION_ERRORS.UNSUPPORTED_PAIR,
    };
  }
  const reverse = convertAllocationQuantity({
    ...dimensions,
    quantity: pricingQuantity,
    sourceUnit: pricingUnit,
    targetUnit: physicalUnit,
  });
  if (reverse.error || reverse.exactValue === null) {
    return {
      valid: false,
      error: reverse.error === 'MISSING_DIMENSIONS'
        ? COMMERCIAL_CONVERSION_ERRORS.MISSING_DIMENSIONS
        : COMMERCIAL_CONVERSION_ERRORS.UNSUPPORTED_PAIR,
    };
  }

  const forwardMatches = roundCommercialQuantity(forward.exactValue) === roundCommercialQuantity(pricingQuantity);
  const reverseMatches = roundCommercialQuantity(reverse.exactValue) === roundCommercialQuantity(physicalQuantity);
  return forwardMatches || reverseMatches
    ? { valid: true, error: null }
    : { valid: false, error: COMMERCIAL_CONVERSION_ERRORS.INCONSISTENT_QUANTITIES };
}

/**
 * Build all derivable views of one operator-entered amount. The source unit is
 * only the way the operator entered this transaction; no component becomes a
 * product-wide warehouse/base unit.
 */
export function createQuantitySet(
  quantity: number,
  unit: AllocationUnit,
  dimensions: ConversionSource,
): QuantitySet {
  const numericQuantity = Number(quantity);
  const result: QuantitySet = { pcs: null, m2: null, m3: null };
  if (!Number.isFinite(numericQuantity)) return result;

  const sign = numericQuantity < 0 ? -1 : 1;
  const absoluteQuantity = Math.abs(numericQuantity);
  if (absoluteQuantity === 0) return { pcs: 0, m2: 0, m3: 0 };

  COMMERCIAL_UNITS.forEach((targetUnit) => {
    if (targetUnit === unit) {
      result[targetUnit] = roundCommercialQuantity(numericQuantity);
      return;
    }
    const converted = convertCommercialQuantity(absoluteQuantity, unit, targetUnit, dimensions);
    if (!converted.error && converted.exactValue !== null) {
      result[targetUnit] = roundCommercialQuantity(converted.exactValue * sign);
    }
  });
  return result;
}

/**
 * Frontend mirror of backend buildQuantityEquivalentsFromPair. Prefer a
 * validated explicit conversion snapshot (including deliberate whole-piece
 * rounding), then the commercial price pair, and finally the entered side.
 */
export function buildQuantitySetFromPair(
  value: CommercialQuantityValue,
  dimensions: ConversionSource,
): QuantitySet | null {
  if (!isAllocationUnit(value.physicalUnit)) return null;

  // Once any component was persisted, the set is atomic: a NULL sibling means
  // "not derivable at the time", not "recalculate it from today's catalog".
  // A deliberate edit produces a fresh value without these fields and is then
  // validated/rebuilt from the newly accepted pair below.
  if (hasPersistedQuantitySet(value as unknown as Record<string, unknown>)) {
    return quantitySetFromFields(value as unknown as Record<string, unknown>);
  }

  const candidates = [
    { quantity: value.conversionQuantity, unit: value.conversionUnit },
    { quantity: value.pricingQuantity, unit: value.pricingUnit },
  ];
  for (const candidate of candidates) {
    if (
      !isAllocationUnit(candidate.unit)
      || candidate.unit === value.physicalUnit
      || !isPositiveCommercialQuantity(candidate.quantity)
    ) {
      continue;
    }
    const validation = validateCommercialQuantityPair(
      value.physicalQuantity,
      value.physicalUnit,
      Number(candidate.quantity),
      candidate.unit,
      dimensions,
    );
    if (validation.valid) {
      return createQuantitySet(Number(candidate.quantity), candidate.unit, dimensions);
    }
  }

  return createQuantitySet(value.physicalQuantity, value.physicalUnit, dimensions);
}

export function quantitySetField(prefix: string, unit: AllocationUnit): string {
  const suffix = unit === 'pcs' ? 'Pcs' : unit.toUpperCase();
  return `${prefix}${suffix}`;
}

export function quantitySetFromFields(
  source: Record<string, unknown> | null | undefined,
  prefix = 'quantity',
): QuantitySet {
  return COMMERCIAL_UNITS.reduce<QuantitySet>((set, unit) => {
    const rawValue = source?.[quantitySetField(prefix, unit)];
    const value = rawValue === null || rawValue === undefined || rawValue === ''
      ? Number.NaN
      : Number(rawValue);
    set[unit] = Number.isFinite(value) ? roundCommercialQuantity(value) : null;
    return set;
  }, { pcs: null, m2: null, m3: null });
}

export function hasPersistedQuantitySet(
  source: Record<string, unknown> | null | undefined,
  prefix = 'quantity',
): boolean {
  return Object.values(quantitySetFromFields(source, prefix)).some((value) => value !== null);
}

export function quantitySetToFields(set: QuantitySet, prefix = 'quantity'): Record<string, number | null> {
  return COMMERCIAL_UNITS.reduce<Record<string, number | null>>((fields, unit) => {
    fields[quantitySetField(prefix, unit)] = set[unit] === null
      ? null
      : roundCommercialQuantity(Number(set[unit]));
    return fields;
  }, {});
}

/**
 * One shared presentation for an atomic quantity set. A screen may supply the
 * historical scalar as a fallback, but it must never attach a made-up unit to
 * that scalar. Every available dimensional view is rendered side by side.
 */
export function formatQuantitySet(
  source: Record<string, unknown> | null | undefined,
  prefix = 'quantity',
  language = 'pl',
  options: FormatQuantitySetOptions = {},
): string {
  const set = quantitySetFromFields(source, prefix);
  const views = COMMERCIAL_UNITS.flatMap((unit) => {
    const value = getQuantitySetValue(set, unit);
    if (value === null) return [];
    const displayValue = options.absolute ? Math.abs(value) : value;
    return [`${formatLocalizedQuantity(displayValue, language)} ${formatUnit(unit, language)}`];
  });
  if (views.length > 0) return views.join(' / ');

  const fallback = Number(options.fallbackQuantity);
  if (!Number.isFinite(fallback)) return options.emptyText ?? '—';
  const displayFallback = options.absolute ? Math.abs(fallback) : fallback;
  const label = options.fallbackUnit ? ` ${formatUnit(options.fallbackUnit, language)}` : '';
  return `${formatLocalizedQuantity(displayFallback, language)}${label}`;
}

export function resolveStoredQuantitySet(
  source: Record<string, unknown>,
  options: { prefix?: string; quantityField?: string; unitField?: string } = {},
): QuantitySet {
  const prefix = options.prefix || 'quantity';
  const quantityField = options.quantityField || 'quantity';
  const unitField = options.unitField || 'unit';
  const persisted = quantitySetFromFields(source, prefix);
  const rawUnit = source[unitField];
  // normalizeQuantityUnit deliberately defaults an empty value to pieces for
  // interactive calculators. Persisted data must be stricter: an unknown or
  // missing code cannot be silently relabelled as `pcs`.
  const normalizedUnit = typeof rawUnit === 'string' ? normalizeUnitCode(rawUnit) : null;
  const unit = isAllocationUnit(normalizedUnit) ? normalizedUnit : null;
  if (Object.values(persisted).some((value) => value !== null)) {
    return persisted;
  }
  return unit
    ? createQuantitySet(Number(source[quantityField]), unit, source as ConversionSource)
    : { pcs: null, m2: null, m3: null };
}

export function getQuantitySetValue(set: QuantitySet, unit: AllocationUnit): number | null {
  const value = set[unit];
  return value === null || !Number.isFinite(Number(value))
    ? null
    : roundCommercialQuantity(Number(value));
}

/** Resolve one persisted component without duplicating Pcs/M2/M3 field maps. */
export function getPersistedQuantityForUnit(
  source: Record<string, unknown> | null | undefined,
  unit: AllocationUnit | null | undefined,
  prefix = 'quantity',
): number | null {
  return unit ? getQuantitySetValue(quantitySetFromFields(source, prefix), unit) : null;
}

export function resolveSharedQuantityUnit(
  left: QuantitySet,
  right: QuantitySet,
  preferredUnit?: AllocationUnit | null,
): AllocationUnit | null {
  const candidates = preferredUnit
    ? [preferredUnit, ...COMMERCIAL_UNITS.filter((unit) => unit !== preferredUnit)]
    : [...COMMERCIAL_UNITS];
  return candidates.find((unit) => (
    getQuantitySetValue(left, unit) !== null
    && getQuantitySetValue(right, unit) !== null
  )) || null;
}

export function scaleQuantitySet(set: QuantitySet, factor: number): QuantitySet {
  if (!Number.isFinite(Number(factor))) return { pcs: null, m2: null, m3: null };
  if (Number(factor) === 0) return { pcs: 0, m2: 0, m3: 0 };
  return COMMERCIAL_UNITS.reduce<QuantitySet>((scaled, unit) => {
    const value = getQuantitySetValue(set, unit);
    scaled[unit] = value === null ? null : roundCommercialQuantity(value * factor);
    return scaled;
  }, { pcs: null, m2: null, m3: null });
}

/**
 * Rebase an immutable set to a new amount expressed in one of its available
 * units. Existing ratios are scaled; missing siblings stay missing.
 */
export function scaleQuantitySetToAmount(
  set: QuantitySet,
  amount: number,
  unit: AllocationUnit,
): QuantitySet | null {
  const roundedAmount = roundCommercialQuantity(Number(amount));
  const currentAmount = getQuantitySetValue(set, unit);
  if (!Number.isFinite(roundedAmount)) return null;
  if (currentAmount === null || Math.abs(currentAmount) < 1e-9) {
    return null;
  }

  const scaled = scaleQuantitySet(set, roundedAmount / currentAmount);
  scaled[unit] = roundedAmount;
  return scaled;
}

export function sumQuantitySets(sets: QuantitySet[]): QuantitySet {
  if (sets.length === 0) return { pcs: 0, m2: 0, m3: 0 };
  return COMMERCIAL_UNITS.reduce<QuantitySet>((sum, unit) => {
    const values = sets.map((set) => getQuantitySetValue(set, unit));
    sum[unit] = values.some((value) => value === null)
      ? null
      : roundCommercialQuantity(values.reduce<number>((total, value) => total + Number(value), 0));
    return sum;
  }, { pcs: null, m2: null, m3: null });
}

export function subtractQuantitySets(minuend: QuantitySet, subtrahend: QuantitySet): QuantitySet {
  return COMMERCIAL_UNITS.reduce<QuantitySet>((difference, unit) => {
    const left = getQuantitySetValue(minuend, unit);
    const right = getQuantitySetValue(subtrahend, unit);
    difference[unit] = left === null || right === null
      ? null
      : roundCommercialQuantity(left - right);
    return difference;
  }, { pcs: null, m2: null, m3: null });
}

export function quantitySetRatio(
  part: QuantitySet,
  whole: QuantitySet,
  preferredUnit?: AllocationUnit | null,
): { ratio: number | null; unit: AllocationUnit | null } {
  const unit = resolveSharedQuantityUnit(part, whole, preferredUnit);
  if (!unit) return { ratio: null, unit: null };

  const partValue = getQuantitySetValue(part, unit);
  const wholeValue = getQuantitySetValue(whole, unit);
  if (partValue === null || partValue < 0 || wholeValue === null || wholeValue <= 0) {
    return { ratio: null, unit };
  }

  return { ratio: partValue / wholeValue, unit };
}

export function isQuantitySetEmpty(set: QuantitySet): boolean {
  const values = COMMERCIAL_UNITS
    .map((unit) => getQuantitySetValue(set, unit))
    .filter((value): value is number => value !== null);
  return values.length === 0 || values.every((value) => Math.abs(value) < 0.001);
}

export function isQuantitySetBelowThreshold(
  quantity: QuantitySet,
  threshold: QuantitySet,
  preferredUnit?: AllocationUnit | null,
): { below: boolean; ratio: number | null; unit: AllocationUnit | null } {
  if (isQuantitySetEmpty(threshold)) return { below: false, ratio: null, unit: null };
  const { ratio, unit } = quantitySetRatio(quantity, threshold, preferredUnit);
  return { below: ratio !== null && ratio < 1 - 1e-9, ratio, unit };
}
